import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { BrandLogo } from '@/shared/ui';
import { COMPANY_FACTS, POLICY_LINKS } from './footer-content';

/** 오른쪽에 크게 싣는 항목 — 나머지 사업자 정보는 왼쪽에 작게 한 줄씩 흐른다. */
const PHONE_LABEL = '전화번호';
const HOURS_LABEL = '상담 운영시간';

/**
 * 브랜드 푸터 — 흑연 바탕에 흰 로고, 그 아래 약관 링크 한 줄과 작은 사업자 정보,
 * 오른쪽에 큰 고객센터 번호와 상담 시간. 모든 고객 화면이 이 푸터를 쓴다.
 */
export const SiteFooterBrand = (): ReactElement => {
  const phone = COMPANY_FACTS.find((fact) => fact.label === PHONE_LABEL);
  const hours = COMPANY_FACTS.find((fact) => fact.label === HOURS_LABEL);
  const facts = COMPANY_FACTS.filter(
    (fact) => fact.label !== PHONE_LABEL && fact.label !== HOURS_LABEL,
  );

  return (
    <footer className="bg-primary text-white">
      <div className="px-5 pt-16 pb-14 sm:px-8 sm:pt-20 xl:px-14">
        <BrandLogo className="h-10 sm:h-12" />

        <div className="mt-14 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-col gap-6">
            <ul className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[15px] font-semibold">
              {POLICY_LINKS.map((link, index) => (
                <li key={link.path} className="flex items-center gap-4">
                  {index > 0 ? <span aria-hidden className="h-3 w-px bg-white/30" /> : null}
                  <Link to={link.path} className="underline-offset-4 hover:underline">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            <address className="not-italic">
              <dl className="flex max-w-4xl flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-white/75">
                {facts.map((fact) => (
                  <div key={fact.label} className="flex gap-1.5">
                    <dt>{fact.label}:</dt>
                    <dd className="text-white/90">
                      {fact.href !== undefined ? (
                        <a href={fact.href} className="underline-offset-4 hover:underline">
                          {fact.value}
                        </a>
                      ) : (
                        fact.value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </address>

            <p className="text-[13px] text-white/60">© 2026 주식회사 슈퍼스트럭처</p>
          </div>

          {phone !== undefined ? (
            <div className="flex flex-col gap-3 lg:items-end lg:text-right">
              <p className="flex items-baseline gap-3">
                <span className="text-xl font-semibold">고객센터</span>
                <a
                  href={phone.href}
                  className="text-3xl font-bold tracking-[-0.01em] text-brand-lime tabular-nums underline-offset-4 hover:underline sm:text-4xl"
                >
                  {phone.value}
                </a>
              </p>
              {hours !== undefined ? (
                <p className="text-[15px] text-white/80">{hours.value}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </footer>
  );
};
